<template>
  <div class="cache_manager">
    <div class="container">
      <h1>Cache Management</h1>

      <!-- Status Section -->
      <div class="status_section">
        <h2>Cache Status</h2>

        <div class="status_card" v-if="cacheStatus">
          <div class="status_item">
            <span class="label">Status:</span>
            <span class="value" :class="cacheStatus.is_refreshing ? 'refreshing' : cacheStatus.status">
              {{ cacheStatus.is_refreshing ? "refreshing..." : cacheStatus.status }}
            </span>
          </div>

          <div class="status_item" v-if="cacheStatus.cached_at">
            <span class="label">Last Cached:</span>
            <span class="value">{{ formatDate(cacheStatus.cached_at) }}</span>
          </div>

          <div class="status_item" v-if="cacheStatus.age_hours !== null">
            <span class="label">Cache Age:</span>
            <span class="value">{{ formatAge(cacheStatus.age_hours) }}</span>
          </div>

          <div class="status_item">
            <span class="label">Total Auctions:</span>
            <span class="value">{{
              cacheStatus.total_count?.toLocaleString() || 0
            }}</span>
          </div>

          <div class="status_item" v-if="cacheStatus.last_error">
            <span class="label">Last Error:</span>
            <span class="value error_text">{{ cacheStatus.last_error }}</span>
          </div>
        </div>

        <div class="status_card empty" v-else>
          <p>No cache data available</p>
        </div>
      </div>

      <!-- Actions Section -->
      <div class="actions_section">
        <h2>Actions</h2>

        <div class="actions">
          <button
            @click="checkCacheStatus"
            class="btn btn-check"
            :disabled="loading || isRefreshing"
          >
            {{ loading && currentAction === "check" ? "Checking..." : "Check Status" }}
          </button>

          <button
            @click="refreshCache"
            class="btn btn-refresh"
            :disabled="loading || isRefreshing"
          >
            {{ isRefreshing ? "Refreshing..." : "Refresh Cache" }}
          </button>
        </div>

        <div class="refresh_progress" v-if="isRefreshing">
          <span class="spinner"></span>
          Fetching all auctions from JDM API, this may take a few minutes...
        </div>
      </div>

      <!-- Messages Section -->
      <div class="messages_section" v-if="message">
        <div class="message" :class="message.type">
          <span class="icon">{{ message.type === "success" ? "✓" : "✕" }}</span>
          <span class="text">{{ message.text }}</span>
          <button @click="clearMessage" class="close">×</button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CacheManager",
  data() {
    return {
      cacheStatus: null,
      message: null,
      loading: false,
      currentAction: null,
      isRefreshing: false,
      _pollTimer: null,
    };
  },
  mounted() {
    this.checkCacheStatus();
  },
  beforeUnmount() {
    this._stopPolling();
  },
  methods: {
    async checkCacheStatus() {
      this.loading = true;
      this.currentAction = "check";

      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_HOST + "/admin/cache/status"
        );
        this.cacheStatus = data;
        this.isRefreshing = !!data.is_refreshing;
        if (this.isRefreshing) {
          this._startPolling();
        }
      } catch (error) {
        console.error("Error checking cache status:", error);
        this.showMessage("Failed to check cache status", "error");
      } finally {
        this.loading = false;
        this.currentAction = null;
      }
    },

    async refreshCache() {
      if (!confirm("Start a background cache refresh? This may take a few minutes.")) {
        return;
      }

      this.loading = true;
      this.currentAction = "refresh";

      try {
        const { data } = await axios.post(
          process.env.VUE_APP_API_HOST + "/admin/cache/refresh"
        );

        if (data.status === "already_running") {
          this.showMessage("A refresh is already running.", "info");
          this.isRefreshing = true;
          this._startPolling();
        } else if (data.status === "started") {
          this.showMessage("Cache refresh started in background.", "success");
          this.isRefreshing = true;
          this._startPolling();
        } else {
          this.showMessage(data.message || "Unexpected response", "error");
        }
      } catch (error) {
        console.error("Error starting cache refresh:", error);
        this.showMessage("Failed to start cache refresh", "error");
      } finally {
        this.loading = false;
        this.currentAction = null;
      }
    },

    _startPolling() {
      this._stopPolling();
      this._pollTimer = setInterval(async () => {
        try {
          const { data } = await axios.get(
            process.env.VUE_APP_API_HOST + "/admin/cache/status"
          );
          this.cacheStatus = data;
          this.isRefreshing = !!data.is_refreshing;
          if (!this.isRefreshing) {
            this._stopPolling();
            if (data.last_error) {
              this.showMessage(`Refresh failed: ${data.last_error}`, "error");
            } else {
              this.showMessage(
                `Cache refreshed: ${data.total_count?.toLocaleString() || 0} auctions cached.`,
                "success"
              );
            }
          }
        } catch {
          // silently retry
        }
      }, 3000);
    },

    _stopPolling() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer);
        this._pollTimer = null;
      }
    },

    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch {
        return dateString;
      }
    },

    formatAge(hours) {
      if (hours < 1) {
        const minutes = Math.round(hours * 60);
        return `${minutes} minutes ago`;
      } else if (hours < 24) {
        return `${Math.round(hours)} hours ago`;
      } else {
        const days = Math.round(hours / 24);
        return `${days} days ago`;
      }
    },

    showMessage(text, type = "info") {
      this.message = { text, type };

      // Auto-clear message after 5 seconds
      setTimeout(() => {
        this.clearMessage();
      }, 5000);
    },

    clearMessage() {
      this.message = null;
    },
  },
};
</script>

<style scoped>
.cache_manager {
  background: #f8fafc;
  min-height: 100vh;
  padding: 32px 20px;
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

h1 {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 32px;
}

h2 {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
}

/* Status Section */
.status_section {
  margin-bottom: 32px;
}

.status_card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.status_card.empty {
  text-align: center;
  color: #6b7280;
  padding: 40px 24px;
}

.status_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.status_item:last-child {
  border-bottom: none;
}

.status_item .label {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.status_item .value {
  font-size: 16px;
  font-weight: 500;
  color: #111827;
}

.status_item .value.cached {
  color: #10b981;
  background: #d1fae5;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.status_item .value.no_cache {
  color: #ef4444;
  background: #fee2e2;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

/* Actions Section */
.actions_section {
  margin-bottom: 32px;
}

.actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
}

.btn-check {
  background: #dbeafe;
  color: #1e40af;
}

.btn-check:hover:not(:disabled) {
  background: #bfdbfe;
}

.btn-refresh {
  background: #dcfce7;
  color: #15803d;
}

.btn-refresh:hover:not(:disabled) {
  background: #bbf7d0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Messages Section */
.messages_section {
  margin-bottom: 32px;
}

.message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid;
  animation: slideIn 0.3s ease;
}

.message.success {
  background: #d1fae5;
  border-left-color: #10b981;
  color: #047857;
}

.message.error {
  background: #fee2e2;
  border-left-color: #ef4444;
  color: #dc2626;
}

.message.info {
  background: #dbeafe;
  border-left-color: #3b82f6;
  color: #1e40af;
}

.message .icon {
  font-weight: 700;
  font-size: 18px;
}

.message .text {
  flex: 1;
}

.message .close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.message .close:hover {
  opacity: 1;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stats Section */
.stats_section {
  margin-bottom: 32px;
}

.stats_card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.stat_item:last-child {
  border-bottom: none;
}

.stat_item .label {
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.stat_item .value {
  font-size: 16px;
  font-weight: 700;
  color: #10b981;
}

.status_item .value.refreshing {
  color: #d97706;
  background: #fef3c7;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.error_text {
  color: #dc2626;
  font-size: 14px;
}

.refresh_progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  color: #d97706;
  font-size: 14px;
  font-weight: 500;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #fde68a;
  border-top-color: #d97706;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .cache_manager {
    padding: 20px 16px;
  }

  h1 {
    font-size: 24px;
    margin-bottom: 24px;
  }

  h2 {
    font-size: 16px;
  }

  .status_card,
  .stats_card {
    padding: 16px;
  }

  .actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .status_item,
  .stat_item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
